import { IMfPerfomanceDtrum } from 'interfaces/WidgetParameters';
import { services } from 'services/container';

import { getIsAndroidHybrid, isMobileDevice } from './native';

export const dtrumLeaveAction = (actionId: number): void => {
    const { dtrum } = window;

    if (dtrum) {
        return dtrum.leaveAction(actionId);
    }
};

export const dtrumEnterAction = (actionName: string): number | undefined => {
    const { dtrum } = window;

    if (dtrum) {
        const actionId = dtrum.enterAction(actionName);
        dtrum.actionName(actionName, actionId);

        return actionId;
    }
};

export const dtrumReportError = (error: string | Error, actionId?: number): void => {
    const { dtrum } = window;

    if (dtrum) {
        return dtrum.reportError(error, actionId);
    }
};

export const dtrumReportCustomError = (error: string, actionId: number): void => {
    const { dtrum } = window;

    if (dtrum) {
        return dtrum.reportCustomError('Error', error, undefined, actionId);
    }
};

export const dtrumAddActionProperties = ({
    parentActionId,
    javaLong,
    date,
    shortString,
    javaDouble,
}: Dtrum.PropertyObject & { parentActionId: number }): void => {
    const { dtrum } = window;

    if (dtrum) {
        dtrum.addActionProperties(parentActionId, javaLong, date, shortString, javaDouble);
    }
};

type DtrumInstantAction = {
    // Имя события, аналогичное наименование атрибуту data-action-name
    actionName: string;
    // Опциональный параметр для логирования ошибки
    error?: string | Error;
    actionProperties?: Dtrum.PropertyObject;
};

export const dtrumInstantAction = (action: DtrumInstantAction): void => {
    const { dtrum } = window;

    if (dtrum) {
        const { actionName, error, actionProperties } = action;
        const actionId = dtrum.enterAction(actionName);

        dtrum.actionName(actionName, actionId);

        if (error) {
            dtrum.reportError(error, actionId);
        }

        if (actionProperties) {
            dtrum.addActionProperties(
                actionId,
                actionProperties.javaLong,
                actionProperties.date,
                actionProperties.shortString,
                actionProperties.javaDouble,
            );
        }

        dtrum.leaveAction(actionId);
    }
};

export enum EDtrumPerformanceEvent {
    ACTION = 'ACT', // Начало экшена
    CLICK = 'CLICK', // клик по кнопке

    MF = 'MF', // 1. агрузка микрофронта - MF
    JS = 'JS', // 2. загрузка статики - JS
    INIT = 'INIT', // 3. post init - INIT
    WS = 'WS', // 4. сокет - WS
    HTML = 'HTML', // 5. загрузка верстки - HTML
    AUTH = 'AUTH', // 6. auth - AUTH
    READY = 'RDY', // 7. готов к переписке
}

const DtrumEventMap: Record<EDtrumPerformanceEvent, number> = {
    [EDtrumPerformanceEvent.MF]: 1,
    [EDtrumPerformanceEvent.JS]: 2,
    [EDtrumPerformanceEvent.INIT]: 3,
    [EDtrumPerformanceEvent.WS]: 4,
    [EDtrumPerformanceEvent.HTML]: 5,
    [EDtrumPerformanceEvent.AUTH]: 6,
    [EDtrumPerformanceEvent.READY]: 7,
    [EDtrumPerformanceEvent.ACTION]: 8,
    [EDtrumPerformanceEvent.CLICK]: 9,
};

interface DtrumPerformancePayload {
    name: EDtrumPerformanceEvent;
    dur: number;
    dt: number;
    perf: number;
    click: 0 | 1;
}

type MetricsPlatfrom = 'hybrid' | 'adaptive' | 'web';

enum EMetricsType {
    TIMER = 'TIMER',
    GAUGE = 'GAUGE',
}

export class ChatPerfomance {
    private static actionId: number | null = 0;
    private static usedActionIdSet = new Set();
    private static clicked = false;
    private static mfDtrumPerf: IMfPerfomanceDtrum;
    private static httpClient: HttpClient;
    private static plaform: MetricsPlatfrom;
    private static perfMap: Record<EDtrumPerformanceEvent, number> = {
        [EDtrumPerformanceEvent.ACTION]: 0,
        [EDtrumPerformanceEvent.CLICK]: 0,

        [EDtrumPerformanceEvent.MF]: 0,
        [EDtrumPerformanceEvent.JS]: 0,
        [EDtrumPerformanceEvent.INIT]: 0,
        [EDtrumPerformanceEvent.WS]: 0,
        [EDtrumPerformanceEvent.HTML]: 0,
        [EDtrumPerformanceEvent.AUTH]: 0,
        [EDtrumPerformanceEvent.READY]: 0,
    };
    private static payloadMap: Record<EDtrumPerformanceEvent, DtrumPerformancePayload> = {
        [EDtrumPerformanceEvent.ACTION]: undefined,
        [EDtrumPerformanceEvent.CLICK]: undefined,
        [EDtrumPerformanceEvent.MF]: undefined,
        [EDtrumPerformanceEvent.JS]: undefined,
        [EDtrumPerformanceEvent.INIT]: undefined,
        [EDtrumPerformanceEvent.WS]: undefined,
        [EDtrumPerformanceEvent.HTML]: undefined,
        [EDtrumPerformanceEvent.AUTH]: undefined,
        [EDtrumPerformanceEvent.READY]: undefined,
    };

    public static init = (mfDtrumPerf: IMfPerfomanceDtrum, httpClient: HttpClient) => {
        const platform = getIsAndroidHybrid() ? 'hybrid' : isMobileDevice() ? 'adaptive' : 'web';
        ChatPerfomance.setMfDtrumPerf(mfDtrumPerf);
        ChatPerfomance.setHttpClient(httpClient);
        ChatPerfomance.setPlatform(platform);
        ChatPerfomance.setPerf(EDtrumPerformanceEvent.MF, mfDtrumPerf?.mfPerf);
        ChatPerfomance.addProperties(EDtrumPerformanceEvent.MF);
        ChatPerfomance.addProperties(EDtrumPerformanceEvent.JS);
    };

    public static setPerf = (event: EDtrumPerformanceEvent, performance: number) => {
        ChatPerfomance.perfMap[event] = performance;
    };

    static setMfDtrumPerf = (mfDtrumPerf: IMfPerfomanceDtrum) => {
        if (!mfDtrumPerf) return;

        const { actionId } = mfDtrumPerf;
        ChatPerfomance.mfDtrumPerf = mfDtrumPerf;

        if (ChatPerfomance.usedActionIdSet.has(actionId) || !actionId) return;

        ChatPerfomance.setPerf(EDtrumPerformanceEvent.MF, ChatPerfomance?.mfDtrumPerf?.mfPerf);
        ChatPerfomance.usedActionIdSet.add(actionId);
        ChatPerfomance.actionId = actionId;
        services.log.info(`!WIDGET-METRICS! actionId установлен: ${actionId}`);
    };

    static setClicked = () => {
        services.log.info(`!WIDGET-METRICS! клик на кнопку`);
        ChatPerfomance.perfMap.CLICK = performance.now();
        ChatPerfomance.clicked = true;
    };

    static addProperties = (event: EDtrumPerformanceEvent, duration?: number) => {
        const payload: DtrumPerformancePayload = {
            name: event,
            dur: ChatPerfomance.getDuration(event, duration),
            dt: Date.now(),
            perf: Math.round(performance.now()),
            click: ChatPerfomance.clicked ? 1 : 0,
        };

        ChatPerfomance.payloadMap[event] = payload;
        ChatPerfomance.send(payload.dur, event);

        if (!ChatPerfomance.actionId) return;

        services.log.info(`!WIDGET-METRICS! Добавлено событие ${event}`);

        dtrumAddActionProperties({
            parentActionId: ChatPerfomance.actionId,
            shortString: {
                [`universal_param${DtrumEventMap[event]}`]: JSON.stringify(payload),
            },
        });
    };

    static endAction = () => {
        if (!ChatPerfomance.actionId) return;
        services.log.info(`!WIDGET-METRICS! экшен по ${ChatPerfomance.actionId} закрыт`);

        dtrumLeaveAction(ChatPerfomance.actionId);
        ChatPerfomance.setMfDtrumPerf(null);
    };

    private static getDuration = (event: EDtrumPerformanceEvent, duration_: number) => {
        let duration = duration_;
        switch (event) {
            case EDtrumPerformanceEvent.READY:
                duration = ChatPerfomance.perfMap.CLICK - performance.now();
                break;
            case EDtrumPerformanceEvent.JS:
                duration = ChatPerfomance.perfMap.MF - ChatPerfomance.perfMap.JS;
                break;
            case EDtrumPerformanceEvent.HTML:
                duration = ChatPerfomance.perfMap.JS - performance.now();
                if (ChatPerfomance.perfMap.CLICK)
                    duration = ChatPerfomance.perfMap.CLICK - performance.now();
                break;
            case EDtrumPerformanceEvent.MF:
                duration = ChatPerfomance.perfMap.MF;
                break;
            default:
                break;
        }

        return Math.round(Math.abs(duration));
    };

    public static setHttpClient(client: HttpClient) {
        services.log.info(`!WIDGET-METRICS! Установлен http клиент`);
        ChatPerfomance.httpClient = client;
    }

    private static getHttpClient = (): Promise<HttpClient> => {
        let counter = 0;
        return new Promise((resolve, reject) => {
            if (ChatPerfomance.httpClient) {
                resolve(ChatPerfomance.httpClient);
                return;
            }

            const intervalId = setInterval(() => {
                if (counter > 100) {
                    reject('Failed to get HttpClient');
                }

                if (ChatPerfomance.httpClient) {
                    resolve(ChatPerfomance.httpClient);
                    clearInterval(intervalId);
                }
                counter++;
            }, 500);
        });
    };

    public static setPlatform(plaform_) {
        ChatPerfomance.plaform = plaform_;
    }

    public static send = async (duration: number, event: EDtrumPerformanceEvent) => {
        const httpClient = await ChatPerfomance.getHttpClient();
        services.log.info(
            `!WIDGET-METRICS! Отправлена метрика: ${JSON.stringify({ duration, event } ?? {})}`,
        );
        httpClient.post('/services/shared/mf/metrics', {
            payload: [
                {
                    type: EMetricsType.TIMER,
                    name: `573_web_chat_widget_loading_${event}`,
                    value: duration,
                    tags: {
                        platfrom: ChatPerfomance.plaform,
                    },
                },
            ],
        });
    };
}
